import { AUDIO_PACKET_CONFIG, AUDIO_PACKET_FRAME, audioConfigPacket, audioFramePacket, decodeAudioConfig, decodeAudioFrame, fromBase64 } from "./audio-protocol.js";

const AUDIO_CONFIG = { codec: "opus", sampleRate: 48_000, numberOfChannels: 1, bitrate: 64_000 };

/** Подпись режима микрофона для UI. */
function microphoneStateLabel(voiceActivated) {
  if (voiceActivated) return "microphone: DTX Opus 48 kHz · voice-activated";
  const kbps = Math.round(AUDIO_CONFIG.bitrate / 1000);
  return `microphone: continuous Opus 48 kHz · ${kbps} kbps`;
}
/** Max frames in WebCodecs AudioEncoder queue before waiting. */
const MAX_ENCODE_QUEUE = 48;
/** Max frames in AudioDecoder queue; above this we wait instead of dropping immediately. */
const MAX_DECODE_QUEUE = 48;
/** Сколько пакетов держать, пока декодер временно отстаёт (~0.5 с при 20 мс). */
const MAX_PENDING_DECODE = 32;

/** Encodes microphone AudioData as small Opus frames for one audio subject. */
export class AudioPublisher {
  constructor({ publishPacket, voiceActivated = false, onSpeaking = () => {}, onDiagnostic = () => {}, onError = console.error, onState = () => {} }) {
    this.publishPacket = publishPacket;
    this.voiceActivated = voiceActivated;
    this.onSpeaking = onSpeaking;
    this.onDiagnostic = onDiagnostic;
    this.onError = onError;
    this.onState = onState;
  }

  async start(mediaStream) {
    const track = mediaStream.getAudioTracks()[0];
    if (!track) throw new Error("Microphone track is unavailable.");
    if (!globalThis.AudioEncoder || !globalThis.MediaStreamTrackProcessor) {
      throw new Error("This browser needs WebCodecs AudioEncoder and MediaStreamTrackProcessor for conference audio.");
    }
    const support = await AudioEncoder.isConfigSupported(AUDIO_CONFIG);
    if (!support.supported) throw new Error("Opus audio encoding is not supported by this browser.");
    this.lastConfigAtUs = 0;
    this.decoderConfig = support.config;
    this.streamEpoch = 0;
    // UI: длинный release; отправка Opus: короткий hangover ~200 мс.
    this.detector = new VoiceActivityDetector({
      transmitReleaseUs: 200_000,
      uiReleaseUs: 1_250_000
    });
    this.preroll = new AudioPrerollBuffer(240_000);
    this.captureClock = new AudioCaptureClock();
    this.publishChain = Promise.resolve();
    this.encoder = new AudioEncoder({
      output: (chunk, metadata) => {
        const config = metadata?.decoderConfig || this.decoderConfig;
        if (metadata?.decoderConfig) this.decoderConfig = metadata.decoderConfig;
        const captureTimeUs = this.captureClock.wallTimeForTimestamp(chunk.timestamp);
        // FPS subjects do not retain a decoder configuration. Repeat it like a
        // video keyframe so a participant joining later can start decoding.
        if (config && captureTimeUs - this.lastConfigAtUs >= 1_000_000) {
          this.lastConfigAtUs = captureTimeUs;
          this.enqueuePublish(audioConfigPacket(config, this.streamEpoch));
        }
        this.enqueuePublish(audioFramePacket(chunk, captureTimeUs));
      },
      error: error => this.onError(error)
    });
    this.encoder.configure(support.config);
    this.reader = new MediaStreamTrackProcessor({ track }).readable.getReader();
    this.running = true;
    this.onState({ label: microphoneStateLabel(this.voiceActivated) });
    void this.consume();
  }

  enqueuePublish(packet) {
    // Config and following Opus frames must keep their order on the subject.
    this.publishChain = this.publishChain
      .then(() => this.publishPacket(packet))
      .catch(error => this.onError(error));
  }

  /**
   * Начало фразы после DTX: новый epoch, сброс Opus, preroll.
   * UI speaking обновляется отдельно через uiChange.
   */
  beginSpeech() {
    this.streamEpoch += 1;
    this.lastConfigAtUs = 0;
    // Пауза без пакетов — новый epoch, а не дырка для PLC на приёме.
    this.encoder.reset();
    this.encoder.configure(this.decoderConfig);
    this.encodePreroll();
  }

  /** Waits for a free encoder queue slot instead of silently dropping frames. */
  async waitEncodeSlot() {
    let waited = false;
    while (this.running && this.encoder && this.encoder.encodeQueueSize >= MAX_ENCODE_QUEUE) {
      waited = true;
      await new Promise(resolve => setTimeout(resolve, 2));
    }
    if (waited) this.onDiagnostic({ type: "audio_encode_backpressure" });
  }

  async consume() {
    try {
      while (this.running) {
        const { done, value } = await this.reader.read();
        if (done) break;
        this.captureClock.observe(value);
        const { transmitChange, uiChange } = this.detector.observe(value);
        if (uiChange === true) this.onSpeaking(true);
        if (uiChange === false) this.onSpeaking(false);

        if (this.voiceActivated) {
          if (transmitChange === true) this.beginSpeech();
          if (this.detector.transmitActive) {
            await this.waitEncodeSlot();
            if (this.encoder.encodeQueueSize < MAX_ENCODE_QUEUE) this.encoder.encode(value);
          } else {
            this.preroll.remember(value);
          }
        } else {
          await this.waitEncodeSlot();
          if (this.encoder.encodeQueueSize < MAX_ENCODE_QUEUE) this.encoder.encode(value);
        }
        value.close();
      }
    } catch (error) {
      if (this.running) this.onError(error);
    }
  }

  stop() {
    this.running = false;
    this.reader?.cancel().catch(() => {});
    this.reader = undefined;
    this.preroll?.clear();
    this.preroll = undefined;
    this.captureClock = undefined;
    this.encoder?.close();
    this.encoder = undefined;
  }

  encodePreroll() {
    if (!this.preroll) return;
    for (const audioData of this.preroll.drain()) {
      try {
        if (this.encoder.encodeQueueSize < MAX_ENCODE_QUEUE) this.encoder.encode(audioData);
      } finally {
        audioData.close();
      }
    }
  }
}

/**
 * AudioEncoder outputs may be bursty. Use the original AudioData timestamp
 * for spacing, but anchor it to Date.now() once so it still lives on the same
 * broad wall-clock axis as the video capture timestamps.
 */
class AudioCaptureClock {
  observe(audioData) {
    if (this.timestampOriginUs !== undefined) return;
    this.timestampOriginUs = audioData.timestamp;
    this.wallOriginUs = Date.now() * 1000;
  }

  wallTimeForTimestamp(timestampUs) {
    if (this.timestampOriginUs === undefined) {
      this.timestampOriginUs = timestampUs;
      this.wallOriginUs = Date.now() * 1000;
    }
    return this.wallOriginUs + (timestampUs - this.timestampOriginUs);
  }
}

/** Keeps a tiny chunk of pre-speech audio so VAD does not chop word starts. */
class AudioPrerollBuffer {
  constructor(maxDurationUs) {
    this.maxDurationUs = maxDurationUs;
    this.frames = [];
    this.durationUs = 0;
  }

  remember(audioData) {
    if (typeof audioData.clone !== "function") return;
    const clone = audioData.clone();
    const durationUs = audioData.duration || Math.round(audioData.numberOfFrames / audioData.sampleRate * 1_000_000);
    this.frames.push({ audioData: clone, durationUs });
    this.durationUs += durationUs;
    while (this.durationUs > this.maxDurationUs && this.frames.length > 1) {
      const dropped = this.frames.shift();
      this.durationUs -= dropped.durationUs;
      dropped.audioData.close();
    }
  }

  drain() {
    const frames = this.frames.map(frame => frame.audioData);
    this.frames = [];
    this.durationUs = 0;
    return frames;
  }

  clear() {
    for (const frame of this.frames) frame.audioData.close();
    this.frames = [];
    this.durationUs = 0;
  }
}

/**
 * Energy VAD: две шкалы release.
 * transmit — короткий hangover для DTX; ui — длинный, чтобы лампочка не мигала.
 */
class VoiceActivityDetector {
  constructor({ transmitReleaseUs = 200_000, uiReleaseUs = 1_250_000 } = {}) {
    this.transmitActive = false;
    this.uiSpeaking = false;
    this.voiceUs = 0;
    this.silenceUs = 0;
    this.threshold = 0.006;
    this.attackUs = 70_000;
    this.transmitReleaseUs = transmitReleaseUs;
    this.uiReleaseUs = uiReleaseUs;
  }

  /**
   * @returns {{ transmitChange?: boolean, uiChange?: boolean }}
   */
  observe(audioData) {
    const samples = new Float32Array(audioData.numberOfFrames);
    audioData.copyTo(samples, { planeIndex: 0, format: "f32-planar" });
    let energy = 0;
    for (const sample of samples) energy += sample * sample;
    const rms = Math.sqrt(energy / Math.max(1, samples.length));
    const durationUs = audioData.duration || Math.round(audioData.numberOfFrames / audioData.sampleRate * 1_000_000);
    /** @type {{ transmitChange?: boolean, uiChange?: boolean }} */
    const result = {};

    if (rms >= this.threshold) {
      this.voiceUs += durationUs;
      this.silenceUs = 0;
      if (!this.transmitActive && this.voiceUs >= this.attackUs) {
        this.transmitActive = true;
        result.transmitChange = true;
      }
      if (!this.uiSpeaking && this.voiceUs >= this.attackUs) {
        this.uiSpeaking = true;
        result.uiChange = true;
      }
      return result;
    }

    this.voiceUs = 0;
    if (!this.transmitActive && !this.uiSpeaking) return result;
    this.silenceUs += durationUs;
    if (this.transmitActive && this.silenceUs >= this.transmitReleaseUs) {
      this.transmitActive = false;
      result.transmitChange = false;
    }
    if (this.uiSpeaking && this.silenceUs >= this.uiReleaseUs) {
      this.uiSpeaking = false;
      result.uiChange = false;
    }
    return result;
  }
}

/** Stable decoder key without changing description every second. */
function decoderSignature(config) {
  return `${config.codec}:${config.sampleRate}:${config.numberOfChannels}:${config.streamEpoch}`;
}

/** Decodes one remote Opus subject and hands timestamped PCM to the mixer. */
export class AudioReceiver {
  constructor({ onAudioData, onAudioDiagnostic = () => {}, onWarning = console.warn }) {
    this.onAudioData = onAudioData;
    this.onAudioDiagnostic = onAudioDiagnostic;
    this.onWarning = onWarning;
    this.tracks = new Map();
    /** Frames before first Opus config. */
    this.earlyFrames = new Map();
  }

  receive(channel, payload) {
    if (payload[0] === AUDIO_PACKET_CONFIG) {
      this.configure(channel, decodeAudioConfig(payload));
      this.flushEarly(channel);
    } else if (payload[0] === AUDIO_PACKET_FRAME) this.decode(channel, payload);
  }

  /** Decodes frames that arrived before config. */
  flushEarly(channel) {
    const queued = this.earlyFrames.get(channel);
    if (!queued?.length) return;
    this.earlyFrames.delete(channel);
    for (const payload of queued) this.decode(channel, payload);
  }

  configure(channel, config) {
    if (!globalThis.AudioDecoder) throw new Error("This browser does not support WebCodecs AudioDecoder.");
    const signature = decoderSignature(config);
    if (this.tracks.get(channel)?.signature === signature) return;
    this.close(channel);
    const track = { channel, signature, pending: [], draining: false };
    const decoder = new AudioDecoder({
      output: audioData => {
        this.onAudioDiagnostic({ type: "audio_decoded", channel });
        this.onAudioData(channel, audioData);
        this.drainPending(track);
      },
      error: error => {
        this.onAudioDiagnostic({ type: "audio_decoder_error", channel });
        this.onWarning("[conference] audio decoder failed", { channel, error });
      }
    });
    const decoderConfig = { codec: config.codec, sampleRate: config.sampleRate, numberOfChannels: config.numberOfChannels };
    if (config.description) decoderConfig.description = fromBase64(config.description).buffer;
    decoder.configure(decoderConfig);
    track.decoder = decoder;
    this.tracks.set(channel, track);
    this.onAudioDiagnostic({ type: "audio_decoder_configured", channel });
  }

  decode(channel, payload) {
    const track = this.tracks.get(channel);
    const frame = decodeAudioFrame(payload);
    if (!track || !frame) {
      if (!track && frame) {
        let queued = this.earlyFrames.get(channel);
        if (!queued) {
          queued = [];
          this.earlyFrames.set(channel, queued);
        }
        if (queued.length < 24) {
          queued.push(payload.slice());
          this.onAudioDiagnostic({ type: "audio_early_buffered", channel });
          return;
        }
      }
      this.onAudioDiagnostic({ type: "audio_dropped_no_decoder", channel });
      return;
    }
    if (track.decoder.decodeQueueSize >= MAX_DECODE_QUEUE) {
      if (track.pending.length >= MAX_PENDING_DECODE) {
        this.onAudioDiagnostic({ type: "audio_dropped_pending_full", channel });
        return;
      }
      track.pending.push(payload);
      this.onAudioDiagnostic({ type: "audio_pending_decode", channel });
      return;
    }
    this.decodeFrame(track, channel, frame);
  }

  /** Decodes one Opus frame. */
  decodeFrame(track, channel, frame) {
    track.captureTimeByTimestamp ??= new Map();
    track.captureTimeByTimestamp.set(frame.timestamp, frame.captureTimeUs);
    try {
      track.decoder.decode(new EncodedAudioChunk({ type: "key", timestamp: frame.timestamp, duration: frame.duration, data: frame.data }));
    } catch (error) {
      this.onAudioDiagnostic({ type: "audio_dropped_decode_exception", channel });
      this.onWarning("[conference] dropped audio packet", { channel, error });
    }
  }

  /** After decoder output, drains the deferred packet queue. */
  drainPending(track) {
    if (track.draining || track.pending.length === 0) return;
    if (track.decoder.decodeQueueSize >= MAX_DECODE_QUEUE) return;
    track.draining = true;
    try {
      while (track.pending.length > 0 && track.decoder.decodeQueueSize < MAX_DECODE_QUEUE) {
        const payload = track.pending.shift();
        const frame = decodeAudioFrame(payload);
        if (!frame) continue;
        this.decodeFrame(track, track.channel, frame);
      }
    } finally {
      track.draining = false;
    }
  }

  captureTime(channel, timestamp) {
    const track = this.tracks.get(channel);
    const value = track?.captureTimeByTimestamp?.get(timestamp);
    track?.captureTimeByTimestamp?.delete(timestamp);
    return value ?? Date.now() * 1000;
  }

  close(channel) { const track = this.tracks.get(channel); if (track) { track.decoder.close(); this.tracks.delete(channel); } }
  closeAll() { for (const channel of this.tracks.keys()) this.close(channel); }
}
